'use client'

import { useState } from 'react'

import { brand } from '@/utils/brand'
import { getV5ApiUrl } from '@/utils/urlHelpers'

export interface OtpSessionResponse {
  id: string
  first_name: string
  last_name: string
  email: string
  phone_number: string
  auth_token: string
  image?: string | null
  digital_label: boolean
  terms: boolean
  pro_user: boolean
  mobile_verification_eligible: boolean
  mobile_verified: boolean
  roles: string[]
}

interface ErrorResponse {
  errors?: string[]
  message?: string
}

export const useOtpLogin = (): {
  requesting: boolean
  verifying: boolean
  requestError: string | null
  verifyError: string | null
  requestOtp: (email: string) => Promise<boolean>
  verifyOtp: (email: string, code: string, cartToken?: string) => Promise<OtpSessionResponse | null>
  clearErrors: () => void
} => {
  const [requesting, setRequesting] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [requestError, setRequestError] = useState<string | null>(null)
  const [verifyError, setVerifyError] = useState<string | null>(null)

  const clearErrors = (): void => {
    setRequestError(null)
    setVerifyError(null)
  }

  const requestOtp = async (email: string): Promise<boolean> => {
    setRequestError(null)
    setRequesting(true)
    try {
      const url = getV5ApiUrl('/sessions/otp/request')
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-brand-id': brand.key,
        },
        body: JSON.stringify({ email, brand_id: brand.key, frontend_app: true }),
      })

      if (!response.ok) {
        const data = (await response.json()) as ErrorResponse
        throw new Error(data.message || data.errors?.[0] || 'Failed to send code.')
      }

      return true
    } catch (err) {
      setRequestError(err instanceof Error ? err.message : 'Failed to send code. Please try again.')
      return false
    } finally {
      setRequesting(false)
    }
  }

  const verifyOtp = async (
    email: string,
    code: string,
    cartToken?: string,
  ): Promise<OtpSessionResponse | null> => {
    setVerifyError(null)
    setVerifying(true)
    try {
      const url = getV5ApiUrl('/sessions/otp/verify')
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-brand-id': brand.key,
        },
        body: JSON.stringify({
          email,
          code,
          brand_id: brand.key,
          frontend_app: true,
          cart_token: cartToken,
        }),
      })

      if (response.ok) {
        return (await response.json()) as OtpSessionResponse
      }

      let message = 'Invalid or expired code.'
      try {
        const errorData = (await response.json()) as ErrorResponse
        message = errorData.message || errorData.errors?.[0] || message
      } catch {
        // Response was not valid JSON, use default message
      }
      throw new Error(message)
    } catch (err) {
      setVerifyError(
        err instanceof Error ? err.message : 'Invalid or expired code. Please try again.',
      )
      return null
    } finally {
      setVerifying(false)
    }
  }

  return { requesting, verifying, requestError, verifyError, requestOtp, verifyOtp, clearErrors }
}
